import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import JsonLd from '../../components/JsonLd/JsonLd';
import MainNav from '../../components/MainNav/MainNav';
import Footer from '../../components/Footer/Footer';
import { SERVICE_PILLARS } from '../../data/servicePillars';
import './CapacidadesPage.css';

const PAGE_URL = 'https://e-asy.es/capacidades';

const CAPACIDADES = [
  {
    id: 'erp-vulpin',
    titulo: 'ERP e implantación',
    descripcion:
      'Software de gestión que se puede operar: módulos, expediente y dato sensible. No un ERP genérico que obliga a cambiarlo todo el día uno.',
    puntos: [
      'Vulpin Suite: Business, Social, Care, Corps y Teams',
      'Implantación por fases y formación al equipo que lo usa',
      'OCR y procesos de gestión repetidos dentro del producto',
      'Si Vulpin no encaja, integramos lo que ya tenéis o buscamos otra base',
    ],
    href: '/vulpin',
    cta: 'Ver Vulpin Suite',
    relacionados: [
      { label: 'Vulpin Suite', href: '/vulpin' },
      { label: 'ERP para pymes', href: '/erp-para-pymes' },
      { label: 'Software gestión ONG', href: '/erp-para-ong' },
      { label: 'Business Central', href: '/servicios/business-central' },
    ],
  },
  {
    id: 'ciberseguridad-cumplimiento',
    titulo: 'Ciberseguridad y cumplimiento',
    descripcion:
      'Perímetro, accesos y norma cuando el dato es de categoría especial. No un pack de antivirus ni un sello ISO que prometamos en tres meses.',
    puntos: [
      'RGPD y AI Act sobre expedientes reales (menores, dependencia, salud)',
      'Quién ve qué: roles, copias restaurables y concienciación del equipo',
      'Criterio on-premise / nube privada cuando el dato no puede salir',
      'ENS si aplica; sin teatro documental que nadie opera',
    ],
    href: '/servicios/ciberseguridad-cumplimiento',
    cta: 'Ver Ciberseguridad y cumplimiento',
    relacionados: [
      { label: 'Ciberseguridad y cumplimiento', href: '/servicios/ciberseguridad-cumplimiento' },
      { label: 'Cumplimiento IA (Art. 4)', href: '/cumplimiento-ia' },
      { label: 'IA privada', href: '/ia-privada' },
    ],
  },
  {
    id: 'ia-produccion',
    titulo: 'IA que se puede operar',
    descripcion:
      'El prototipo en ChatGPT, Cursor o no-code no es un producto. Aquí está el suelo: backend, API, entorno y un responsable después del go-live.',
    puntos: [
      'De demo a servicio: usuarios, claves y un sitio donde vive',
      'Integración con CRM, WhatsApp, ERP o reservas',
      'Azure u otro entorno acordado; on-premise si el dato no puede salir',
      'Retainer de mantenimiento: que el viernes no se caiga el bot',
    ],
    href: '/servicios/ia-en-produccion',
    cta: 'Ver IA en producción',
    relacionados: [
      { label: 'IA en producción', href: '/servicios/ia-en-produccion' },
      { label: 'Agentes IA', href: '/agentes-ia' },
      { label: 'IA privada', href: '/ia-privada' },
    ],
  },
  {
    id: 'web-ecommerce',
    titulo: 'Web y e-commerce conectados',
    descripcion:
      'Web o tienda que habla con el ERP y el inventario. No una landing que se queda desactualizada a la semana.',
    puntos: [
      'Web corporativa, tienda y aplicaciones a medida',
      'Conexión con catálogo, pedidos y sistemas de gestión',
      'Hosting en Azure, AWS, Vercel, Netlify o Docker, según el caso',
      'Formularios, contenidos y un panel que el equipo puede usar',
    ],
    href: '/servicios/desarrollo-web-ecommerce',
    cta: 'Ver Desarrollo web y e-commerce',
    relacionados: [
      { label: 'Desarrollo web y e-commerce', href: '/servicios/desarrollo-web-ecommerce' },
      { label: 'Vulpin Suite', href: '/vulpin' },
    ],
  },
  {
    id: 'cloud-identidad',
    titulo: 'Cloud, identidad y operación',
    descripcion:
      'Equipo tecnológico externo: cuentas (365 / Workspace), infraestructura de proyectos en Azure o AWS, y alguien que responde. No solo un alta de Outlook.',
    puntos: [
      'Microsoft 365, Google Workspace y Business Central cuando encaja',
      'Infraestructura de proyectos: entornos, accesos y costes a la vista',
      'Operación continua sin montar un departamento IT interno',
      'Roadmap Microsoft Solutions Partner en curso; el trabajo, no el sello',
    ],
    href: '/servicios/consultoria-microsoft-azure',
    cta: 'Ver Consultoría Microsoft / Azure',
    relacionados: [
      { label: 'Microsoft / Azure / Workspace', href: '/servicios/consultoria-microsoft-azure' },
      { label: 'Consultoría Azure', href: '/servicios/azure' },
      { label: 'Business Central', href: '/servicios/business-central' },
    ],
  },
  {
    id: 'integraciones',
    titulo: 'Integraciones entre sistemas',
    descripcion:
      'Capacidad transversal: que el ERP, la web, el correo y el dato no vivan en islas. APIs y conectores al servicio de los pilares, no un producto suelto.',
    puntos: [
      'APIs y sincronización entre Vulpin, 365, tienda e inventario',
      'Conectores a contabilidad, almacén o RRHH cuando hace falta',
      'Menos Excel paralelo y menos doble tecleo',
      'Si el núcleo es reporting, la ficha de BI cubre dashboards',
    ],
    href: '/servicios/integraciones-empresariales',
    cta: 'Ver Integraciones empresariales',
    relacionados: [
      { label: 'Integraciones empresariales', href: '/servicios/integraciones-empresariales' },
      { label: 'Vulpin Suite', href: '/vulpin' },
      { label: 'BI y explotación de datos', href: '/servicios/bi-explotacion-datos' },
    ],
  },
];

const CapacidadesPage = ({ darkMode, toggleDarkMode }) => {
  const breadcrumbSchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Inicio', item: 'https://e-asy.es/' },
        { '@type': 'ListItem', position: 2, name: 'Capacidades', item: PAGE_URL },
      ],
    }),
    []
  );

  const itemListSchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Capacidades técnicas de E-ASY',
      description:
        'Stack técnico detrás de los pilares: ERP Vulpin, ciberseguridad, IA en producción, web y e-commerce, cloud Microsoft / Azure / Workspace e ingeniería a cuota.',
      url: PAGE_URL,
      numberOfItems: CAPACIDADES.length,
      itemListElement: CAPACIDADES.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.titulo,
        url: `${PAGE_URL}#${item.id}`,
        description: item.descripcion,
      })),
    }),
    []
  );

  return (
    <>
      <SEO
        title="Capacidades técnicas detrás de Vulpin, IA y Microsoft 365 | E-ASY"
        description="Cómo lo hacemos: ERP, ciberseguridad, IA en producción, web conectada al negocio, cloud (365, Azure, Workspace) e ingeniería a cuota. Consultoría en Tenerife."
        keywords="capacidades técnicas E-ASY, ERP Vulpin Tenerife, IA en producción, Microsoft 365 Azure pymes, ciberseguridad RGPD Canarias, desarrollo web conectado ERP"
        canonical={PAGE_URL}
      />
      <JsonLd id="jsonld-capacidades-breadcrumb" data={breadcrumbSchema} />
      <JsonLd id="jsonld-capacidades-list" data={itemListSchema} />
      <MainNav darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
      <div className={`capacidades-page ${darkMode ? 'dark' : 'light'}`}>
        <section className="capacidades-hero">
          <div className="capacidades-hero-content">
            <div className="capacidades-hero-text">
              <h1 className="capacidades-hero-title">Capacidades técnicas</h1>
              <p className="capacidades-hero-subtitle">
                Los <Link to="/servicios">servicios</Link> son lo que contratáis.
                Aquí está el suelo: con qué lo sostenemos. ERP, seguridad, IA que
                se puede operar, web conectada y cloud para pymes sin un
                departamento IT interno.
              </p>
              <p className="capacidades-hero-subtitle">
                No es un segundo catálogo comercial ni un listado de logos.
                Cada capacidad alimenta uno o más de los{' '}
                <strong>pilares de servicio</strong>.
              </p>
            </div>
            <div className="capacidades-hero-video">
              <div className="capacidades-hero-visual">
                <video
                  src="/videos/hero-capacidades-v1.mp4"
                  className="capacidades-hero-video"
                  autoPlay
                  muted
                  loop
                  playsInline
                  aria-label="Capacidades técnicas de E-ASY"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="capacidades-grid-section">
          <div className="capacidades-grid-content">
            <div className="capacidades-grid-header">
              <h2 className="capacidades-grid-title">
                Lo que sostiene los pilares
              </h2>
              <p className="capacidades-grid-subtitle">
                {SERVICE_PILLARS.map((pilar, index) => (
                  <span key={pilar.id}>
                    {index > 0 && (index === SERVICE_PILLARS.length - 1 ? ' y ' : ', ')}
                    <Link to={pilar.href}>{pilar.title}</Link>
                  </span>
                ))}
                . Si buscáis el detalle comercial, id a cada ficha.
              </p>
            </div>
            <div className="capacidades-grid">
              {CAPACIDADES.map((capacidad) => (
                <article
                  key={capacidad.id}
                  id={capacidad.id}
                  className="capacidad-card"
                >
                  <h3 className="capacidad-title">{capacidad.titulo}</h3>
                  <p className="capacidad-description">{capacidad.descripcion}</p>

                  <div className="capacidad-caracteristicas">
                    <h4 className="caracteristicas-title">Qué cubre</h4>
                    <ul className="caracteristicas-list">
                      {capacidad.puntos.map((punto) => (
                        <li key={punto} className="caracteristica-item">
                          {punto}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="capacidad-casos">
                    <h4 className="casos-title">Se usa en</h4>
                    <ul className="capacidad-relacionados">
                      {capacidad.relacionados.map((item) => (
                        <li key={item.href}>
                          <Link to={item.href} className="capacidad-pilar-link">
                            {item.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Link to={capacidad.href} className="capacidad-cta-button">
                    {capacidad.cta}
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="capacidades-cta-section">
          <div className="capacidades-cta-content">
            <h2 className="capacidades-cta-title">
              ¿Encaja con un pilar o hay que combinar varios?
            </h2>
            <p className="capacidades-cta-subtitle">
              Lo habitual es mezclar: Vulpin con ciberseguridad, un prototipo de
              IA con Azure, o la web con el ERP. Lo vemos en una consultoría
              gratuita, sin montar un proyecto de seis meses para decidirlo.
            </p>
            <div className="capacidades-cta-buttons">
              <Link to="/contacto" className="cta-button primary">
                Agenda una consultoría gratuita
              </Link>
              <Link to="/servicios" className="cta-button secondary">
                Ver los pilares
              </Link>
            </div>
          </div>
        </section>
      </div>
      <Footer darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
    </>
  );
};

export default CapacidadesPage;
